import type {
  MultiSelectProps,
} from '@NitrexGroup/primevue/multiselect';
import {
  CSSProperties,
} from 'vue';

export type TMultiselectProps = MultiSelectProps;

export type TMultiselectExpose = {
  hide: () => void,
  show?: () => void,
};

type NormalizedClass = Record<string, boolean>;
type ClassValue = string | NormalizedClass | (string | NormalizedClass)[];

export type HTMLProperties = {
  style?: string | CSSProperties,
  class?: ClassValue,
};

/**
 * Converts a Vue-compatible `class` binding value into a normalized object
 * that maps class names to boolean flags.
 *
 * This is useful when you want to combine multiple `class` values (strings, arrays, or objects)
 * using object spread syntax (`...`)—for example, when composing class bindings dynamically.
 *
 * Supported input types:
 * - `string`: e.g., `'btn active'` → `{ btn: true, active: true }`
 * - `string[]`: e.g., `['btn', 'active']` → `{ btn: true, active: true }`
 * - `Record<string, boolean>`: remains unchanged
 * - Nested arrays and combinations of the above
 *
 * Example:
 * ```ts
 * const base = normalizeClass('btn primary');
 * const extra = normalizeClass(['active', { disabled: false }]);
 * const combined = { ...base, ...extra };
 * ```
 *
 * @param value - The class value to normalize.
 * @returns An object mapping class names to booleans for easy merging.
 */
export function normalizeClass(value?: ClassValue): NormalizedClass {
  const result: NormalizedClass = {};

  if (!value) {
    return result;
  }

  if (typeof value === 'string') {
    value.split(' ').forEach((cls) => {
      if (cls.trim()) {
        result[cls.trim()] = true;
      }
    });
  } else if (Array.isArray(value)) {
    value.forEach((item) => {
      Object.assign(result, normalizeClass(item));
    });
  } else if (typeof value === 'object') {
    Object.entries(value).forEach(([
      key,
      val,
    ]) => {
      result[key] = Boolean(val);
    });
  }

  return result;
}
